import { Pipe, PipeTransform } from '@angular/core';
import { Subscription } from 'rxjs';
import { LanguageService } from '../i18n/language.service';
import { LoggingService } from '../utils/logging.service';

/**
 * Spezifische Pipe-Implementierung um Datumsangaben gemäß der gewählten Sprache des Nutzers zu formatieren.
 *
 * Die Kommponente unterscheidet folgende Modi:
 *
 *  - customDateTime:'date'           Formatierung als Datumswert, ohne Angabe der Uhrzeit
 *  - customDateTime:'datetime'       Formatierung als Datum mit zusätzlicher Zeitangabe ohne Sekunden
 *  - customDateTime:'dateShortMonth' Formatierung als Monat und Jahr
 *  - customDateTime:'timestamp'      Formatierung als Datum mit zusätzlicher Zeitangabe inkl. Sekunden
 *  - customDateTime:'time'           Formatierung als Zeit mit Sekunden
 *  - customDateTime:'timeNoSeconds'  Formatierung als Zeit ohne Sekunden
 *
 * @author: mgru
 */
@Pipe({
    name: 'customDateTime'
})
export class CustomDateTimePipe implements PipeTransform {

    // Wenn Format für Stunden und Minuten angegeben ist, werden die Sekunden nicht angezeigt
    private static readonly optionsDateTime = {
        week: 'long', year: 'numeric', month: '2-digit',
        day: '2-digit', hour: '2-digit', minute: '2-digit'
    } as const;

    // Format ohne Einschränkung bei der Uhrzeit, hier entscheidet die Methode
    private static readonly optionsTimestamp = {
        week: 'long', year: 'numeric', month: '2-digit',
        day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
    } as const;

    // Formatierung des Datums ohne Angabe einer Uhrzeit
    private static readonly optionsDateOnly = {
        week: 'long', year: 'numeric', month: '2-digit',
        day: '2-digit'
    } as const;

    private static readonly optionsDateShortMonth: Intl.DateTimeFormatOptions = {
        month: 'long', year: 'numeric'
    };

    // Formatierung nur Uhrzeit
    private static readonly optionsTime = {
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    } as const;

    // Formatierung nur Uhrzeit ohne Sekunden
    private static readonly optionsTimeNoSeconds = {
        hour: '2-digit', minute: '2-digit'
    } as const;

    private languageSubscription: Subscription;

    private format: string;

    private suffix: string;

    constructor(private languageService: LanguageService,
                private loggingService: LoggingService) {
    }

    transform(value: Date | string | number, format: string, suffix: string = null) {

        if (format) {
            this.format = format;
        }

        if (suffix) {
            this.suffix = suffix;
        }

        if (value) {
            const date: Date = value instanceof Date ? value : new Date(value);

            if (!date) { // d.valueOf() could also work
                this.loggingService.debug('Es wurde ein Invalides Date an die CustomDateTimePipe übergeben: \'' + date + '\'');
                return null;
            }


            switch (this.format) {
                case 'datetime':
                    return date.toLocaleTimeString(this.languageService.currentLanguage, CustomDateTimePipe.optionsDateTime)
                        + this.getSuffix();
                case 'timestamp':
                    return date.toLocaleTimeString(this.languageService.currentLanguage, CustomDateTimePipe.optionsTimestamp)
                        + this.getSuffix();
                case 'date':
                    return date.toLocaleDateString(this.languageService.currentLanguage, CustomDateTimePipe.optionsDateOnly)
                        + this.getSuffix();
                case 'time':
                    return date.toLocaleTimeString(this.languageService.currentLanguage, CustomDateTimePipe.optionsTime)
                        + this.getSuffix();
                case 'timeNoSeconds':
                    return date.toLocaleTimeString(this.languageService.currentLanguage, CustomDateTimePipe.optionsTimeNoSeconds)
                        + this.getSuffix();
                case 'dateShortMonth':
                    return date.toLocaleDateString(this.languageService.currentLanguage, CustomDateTimePipe.optionsDateShortMonth)
                        + this.getSuffix();
                default:
                    return null;
            }
        }
        return null;
    }

    getSuffix() {
        if (this.suffix) {
            return ' ' + this.suffix;
        } else {
            return '';
        }
    }

    ngOnDestroy() {
        if (this.languageSubscription) this.languageSubscription.unsubscribe();
    }
}
